<template>
  <section id="hero" aria-labelledby="hero-heading" class="relative overflow-hidden">
    <!-- Background gradient -->
    <div class="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-blue-50" aria-hidden="true"></div>
    <div class="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(37,99,235,0.12),transparent_60%)]" aria-hidden="true"></div>

    <div class="relative max-w-[1300px] mx-auto px-6 pt-16 pb-20 lg:pt-24 lg:pb-28">
      <div class="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <!-- Left: Copy -->
        <div>
          <span class="inline-flex items-center gap-2 bg-blue-100 text-[#1E3A5F] text-xs font-semibold px-3 py-1.5 rounded-full mb-5">
            <i class="pi pi-bolt text-[10px]"></i>
            {{ badge }}
          </span>

          <h1 id="hero-heading" class="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight tracking-tight">
            One platform to run
            <span class="text-[#1A56DB]">every part</span>
            of your institution.
          </h1>

          <p class="mt-5 text-lg md:text-xl text-gray-600 max-w-xl leading-relaxed">
            {{ appName }} unifies admissions, course registration, billing, examinations and reporting
            — so administrators, teachers, bursars and students all work from a single source of truth.
          </p>

          <div class="mt-8 flex flex-col sm:flex-row gap-3">
            <button
              v-if="!isWhiteLabel"
              type="button"
              class="inline-flex items-center justify-center gap-2 bg-[#1A56DB] hover:bg-[#2563EB] text-white font-semibold px-6 py-3 rounded-md shadow-md transition border-0 cursor-pointer"
              data-testid="header-demo-cta"
              @click="landingStore.openDemo()"
            >
              Request a Demo
              <i class="pi pi-arrow-right" aria-hidden="true"></i>
            </button>
            <Button
              label="Sign In"
              severity="secondary"
              outlined
              class="px-6 py-3 rounded-md font-semibold"
              data-testid="header-signin-cta"
              @click="router.push('/login')"
            />
          </div>
          <p v-if="!isWhiteLabel" class="mt-3 text-sm text-gray-500">
            Or email us at
            <a href="mailto:support@gradeverse.com" class="font-semibold text-[#1A56DB] hover:underline">support@gradeverse.com</a>.
          </p>

          <!-- Trust strip -->
          <ul class="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-gray-500 list-none m-0 p-0">
            <li class="flex items-center gap-2">
              <i class="pi pi-globe text-[#1A56DB]" aria-hidden="true"></i>
              <span>Cloud-hosted, accessible anywhere</span>
            </li>
            <li class="flex items-center gap-2">
              <i class="pi pi-mobile text-[#1A56DB]" aria-hidden="true"></i>
              <span>Mobile-ready</span>
            </li>
          </ul>
        </div>

        <!-- Right: Visual mock -->
        <div class="relative hidden lg:block" aria-hidden="true">
          <div class="absolute -top-6 -right-6 w-72 h-72 bg-blue-200/50 rounded-full blur-3xl"></div>
          <div class="absolute -bottom-10 -left-10 w-72 h-72 bg-blue-300/40 rounded-full blur-3xl"></div>

          <div class="relative bg-white rounded-2xl shadow-2xl border border-gray-100 p-6">
            <div class="flex items-center gap-2 mb-4" aria-hidden="true">
              <span class="w-3 h-3 rounded-full bg-red-400"></span>
              <span class="w-3 h-3 rounded-full bg-yellow-400"></span>
              <span class="w-3 h-3 rounded-full bg-green-400"></span>
              <span class="ml-3 text-xs font-mono text-gray-400">{{ appName.toLowerCase() }}.app/dashboard</span>
            </div>

            <div class="grid grid-cols-3 gap-3 mb-4">
              <div v-for="stat in stats" :key="stat.label" class="bg-gray-50 rounded-lg p-3">
                <p class="text-xs text-gray-500">{{ stat.label }}</p>
                <p class="text-lg font-bold text-gray-900 mt-0.5">{{ stat.value }}</p>
              </div>
            </div>

            <div class="space-y-2">
              <div v-for="row in mockRows" :key="row.label" class="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2.5">
                <div class="flex items-center gap-3">
                  <span class="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold" :class="row.bg">
                    {{ row.initials }}
                  </span>
                  <div>
                    <p class="text-sm font-medium text-gray-800">{{ row.label }}</p>
                    <p class="text-xs text-gray-500">{{ row.sub }}</p>
                  </div>
                </div>
                <span class="text-xs font-semibold px-2 py-1 rounded-full" :class="row.tagClass">
                  {{ row.tag }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { useBranding } from '@/composables/useBranding';
import { useLandingCountry } from '@/composables/landing/useLandingCountry';
import { useLandingStore } from '@/store/landingStore';
import { formatPrice, SAMPLE_TUITION_NGN } from '@/utils/landing/landingPricing';
import { placeName } from '@/utils/landing/countryProfiles';

const { appName, isWhiteLabel } = useBranding();
const router = useRouter();
const landingStore = useLandingStore();
const { profile, prices, isProfiled } = useLandingCountry();

const badge = computed(() => (isProfiled.value
  ? `Built for schools, colleges and universities in ${placeName(profile.value)}`
  : 'Built for K-12 and Tertiary Institutions'));

const stats = [
  { label: 'Active Students', value: '12,840' },
  { label: 'Courses Run', value: '1,240' },
];

const initialsOf = (name: string) => name.split(' ').map((part) => part[0]).join('').slice(0, 2).toUpperCase();

const mockRows = computed(() => [
  {
    initials: initialsOf(profile.value.sampleNames[0]),
    label: profile.value.sampleNames[0],
    sub: 'CSC 401 — Approved',
    tag: 'Registered',
    bg: 'bg-[#1A56DB]',
    tagClass: 'bg-blue-100 text-[#1E3A5F]',
  },
  {
    initials: initialsOf(profile.value.sampleNames[1]),
    label: profile.value.sampleNames[1],
    sub: `Tuition — ${formatPrice(SAMPLE_TUITION_NGN, prices.value)}`,
    tag: 'Paid',
    bg: 'bg-blue-500',
    tagClass: 'bg-blue-100 text-blue-700',
  },
  {
    initials: initialsOf(profile.value.sampleNames[2]),
    label: profile.value.sampleNames[2],
    sub: `CBT — Mid-${profile.value.academicPeriod}`,
    tag: 'Submitted',
    bg: 'bg-amber-500',
    tagClass: 'bg-amber-100 text-amber-700',
  },
]);
</script>
